import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Radio, Wifi, WifiOff, Activity, Users, ArrowLeft, AlertCircle, Eye } from 'lucide-react';
import { WebRTCViewer } from '../lib/webrtc';
import { liveIntelligenceApi } from '../lib/api';

const STATUS_COPY = {
    'idle': 'Initializing...',
    'signaling-connected': 'Connected — waiting for broadcaster...',
    'awaiting-stream': 'Broadcaster online — waiting for stream...',
    'streaming': 'LIVE',
    'no-broadcaster': 'No active broadcaster — try again later',
    'signaling-closed': 'Disconnected',
    'signaling-error': 'Connection error',
};

const LiveWatchPage = () => {
    const { matchId = 'demo-match-1' } = useParams();
    const [status, setStatus] = useState('idle');
    const [score, setScore] = useState({ home: 0, away: 0 });
    const [events, setEvents] = useState([]);
    const [viewerCount, setViewerCount] = useState(0);
    const videoRef = useRef(null);
    const viewerRef = useRef(null);
    const wsStatsRef = useRef(null);

    useEffect(() => {
        // Connect WebRTC viewer
        const viewer = new WebRTCViewer(matchId, {
            onStatus: setStatus,
            onStream: (stream) => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => {});
                }
            }
        });
        viewer.connect();
        viewerRef.current = viewer;

        // Subscribe to live stats WebSocket
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        const ws = new WebSocket(`${wsUrl}/api/live-intelligence/ws/${matchId}`);
        wsStatsRef.current = ws;
        ws.onmessage = (msg) => {
            try {
                const data = JSON.parse(msg.data);
                if (data.type === 'session_state') {
                    if (data.data?.current_score) setScore(data.data.current_score);
                    if (data.data?.events) setEvents(data.data.events);
                } else if (data.type === 'score_update') {
                    setScore({ home: data.data.home, away: data.data.away });
                } else if (data.type === 'event') {
                    setEvents(prev => [data.data, ...prev].slice(0, 20));
                }
            } catch (e) {}
        };

        // Poll viewer count every 3s
        const intv = setInterval(async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webrtc/rooms/${matchId}`);
                const data = await res.json();
                setViewerCount(data.viewer_count || 0);
            } catch (e) {}
        }, 3000);

        return () => {
            viewer.disconnect();
            ws.close();
            clearInterval(intv);
        };
    }, [matchId]);

    const isLive = status === 'streaming';

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <div className="border-b border-white/10 bg-[#0d2847]">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/live"><Button variant="ghost" size="sm" data-testid="watch-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button></Link>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="font-bold text-lg">Live Stream</h1>
                                {isLive
                                    ? <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full animate-pulse" data-testid="watch-live-badge">● LIVE</span>
                                    : <span className="text-xs px-2 py-0.5 bg-gray-500/20 text-gray-400 rounded-full">OFFLINE</span>
                                }
                            </div>
                            <div className="text-xs text-muted-foreground">Match: <code className="bg-white/10 px-1.5 py-0.5 rounded">{matchId}</code></div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <Eye className="w-4 h-4 text-cyan-400" />
                        <span data-testid="watch-viewer-count">{viewerCount}</span>
                        <span className="text-muted-foreground">watching</span>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-white/10">
                            <video ref={videoRef} className="w-full h-full object-contain" autoPlay playsInline muted={false} controls data-testid="watch-video-player" />
                            {!isLive && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0a1628] to-[#0d2847]">
                                    <div className="text-center text-muted-foreground">
                                        <Radio className={`w-12 h-12 mx-auto mb-2 ${status === 'signaling-connected' || status === 'awaiting-stream' ? 'text-cyan-400 animate-pulse' : 'opacity-30'}`} />
                                        <p className="text-sm" data-testid="watch-status-text">{STATUS_COPY[status] || status}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            Peer-to-peer stream via WebRTC. {status === 'streaming' ? 'Direct connection to broadcaster.' : ''}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <Card className="bg-[#0d2847] border-white/10 p-5">
                            <h3 className="font-semibold mb-3 text-sm">Live Score</h3>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="text-center p-3 bg-[#0a1628] rounded">
                                    <div className="text-xs text-muted-foreground">HOME</div>
                                    <div className="text-3xl font-bold text-cyan-400" data-testid="watch-home-score">{score.home}</div>
                                </div>
                                <div className="text-center p-3 bg-[#0a1628] rounded">
                                    <div className="text-xs text-muted-foreground">AWAY</div>
                                    <div className="text-3xl font-bold text-purple-400" data-testid="watch-away-score">{score.away}</div>
                                </div>
                            </div>
                        </Card>

                        <Card className="bg-[#0d2847] border-white/10 p-5">
                            <h3 className="font-semibold mb-3 text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-green-400" /> Live Events</h3>
                            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="watch-events-feed">
                                {events.length === 0 ? (
                                    <p className="text-xs text-muted-foreground text-center py-4">No events yet</p>
                                ) : events.map(e => (
                                    <div key={e.id} className="p-2 bg-[#0a1628] rounded border-l-2 border-cyan-400 text-xs">
                                        <div className="flex items-center gap-1.5 font-medium">
                                            <span className={`px-1 py-0 rounded text-[9px] ${e.team === 'home' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'}`}>{e.team?.toUpperCase()}</span>
                                            <span className="capitalize">{e.type}</span>
                                        </div>
                                        <div className="text-muted-foreground">{e.description}</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveWatchPage;
